import React, { useCallback } from 'react';
import { IPageData } from '../../../../types/rest/IPageData';
import { convertTableSortToQuerySortBy, useDeterminePagination } from '../../util/tableUtilityMethods';
import { ITableChangeState } from 'react-bootstrap-table-next';
import { CardSection } from '@zillow/constellation';
import { ISortedBy } from '../../../../ui-toolkit';
import { FromToTotalConstellation } from '../signingAppointments/FromToTotal';
import { ISigningAppointmentDashboardResponse } from 'types/signingAppointment/ISigningAppointmentDashboardResponse';
import { useSigningAppointmentsColumns } from './signingAppointmentColumns';
import { SigningAppointmentsPaginationSortParams } from './hooks/useManageSigningAppointmentsPagination';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';
import { getSpruceRowClasses, StyledSpruceDataGrid } from '../../util/spruceOrderIndicator';

const defaultSorted: ISortedBy[] = [{ dataField: 'scheduledFor', order: 'desc' }];

const convertTableSortFieldToQuerySortField = (tableSortField?: string) => {
  if (tableSortField === 'fileNumber') {
    return 'order.fileNumber';
  }
  if (tableSortField === 'closingDate') {
    return 'order.closesOn';
  }
  if (tableSortField === 'status') {
    return 'computedStatus';
  }
  return tableSortField ?? 'order.fileNumber';
};

export const SigningAppointmentsTable: React.FC<{
  data: IPageData<ISigningAppointmentDashboardResponse>;
  isFetching: boolean;
  isSuccess: boolean;
  setSigningAppointmentPaginationSortParams: React.Dispatch<
    React.SetStateAction<SigningAppointmentsPaginationSortParams>
  >;
}> = ({ data, isFetching, isSuccess, setSigningAppointmentPaginationSortParams }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const columns = useSigningAppointmentsColumns();
  const pagination = useDeterminePagination(data);
  const onTableChange = useCallback(
    (_: string, tableState: ITableChangeState<ISigningAppointmentDashboardResponse>) => {
      if (isFetching || !isSuccess) {
        return;
      }
      setSigningAppointmentPaginationSortParams({
        pageNumber: tableState.page - 1,
        pageSize: tableState.sizePerPage,
        sortBy: convertTableSortToQuerySortBy(
          tableState.sortField,
          tableState.sortOrder,
          convertTableSortFieldToQuerySortField
        ),
      });
    },
    [isFetching, isSuccess, setSigningAppointmentPaginationSortParams]
  );
  return (
    <CardSection>
      <StyledSpruceDataGrid
        columns={columns}
        keyField={'id'}
        data={data.content}
        remote
        loading={isFetching}
        pagination={pagination}
        onTableChange={onTableChange}
        defaultSorted={defaultSorted}
        zIndex="10"
        rowClasses={(row: ISigningAppointmentDashboardResponse, rowIndex: number) =>
          getSpruceRowClasses({ ...row, isSpruce: row.isSpruce ?? false }, rowIndex, isZillowClosingEnabled)
        }
      />
      <FromToTotalConstellation
        pageSize={data.pageSize}
        pageNumber={data.pageNumber}
        totalElements={data.totalElements}
      />
    </CardSection>
  );
};
